// Load config from window.APP_CONFIG (injected at runtime) or use defaults
const getConfig = () => {
  if (typeof window !== 'undefined' && window.APP_CONFIG) {
    return window.APP_CONFIG;
  }
  return {
    apiUrl: 'http://localhost:8080',
    adminToken: 'dev-secret-token'
  };
};

class ApiClient {
  constructor() {
    const config = getConfig();
    this.baseUrl = config.apiUrl;
    this.token = config.adminToken;
  }

  getToken() {
    // Lazy token retrieval - only prompt when needed
    if (!this.token) {
      this.token = prompt('Enter admin token:');
      if (this.token) {
        localStorage.setItem('adminToken', this.token);
        // Update window config as well
        if (window.APP_CONFIG) {
          window.APP_CONFIG.adminToken = this.token;
        }
      }
    }
    return this.token;
  }

  async request(endpoint, options = {}) {
    const url = `${this.baseUrl}${endpoint}`;
    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${this.getToken()}`,
      ...options.headers,
    };

    // Create AbortController with extended timeout for long-running operations
    // Default browser timeout can be too short for AI generation tasks
    const timeout = options.timeout || 360000; // 6 minutes default (longer than server 5min timeout)
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    const config = {
      ...options,
      headers,
      signal: controller.signal,
    };

    try {
      const response = await fetch(url, config);
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Request failed' }));
        throw new Error(error.error || `HTTP ${response.status}`);
      }

      // Handle 204 No Content
      if (response.status === 204) {
        return null;
      }

      // Try to parse JSON response with better error handling
      try {
        const data = await response.json();
        return data;
      } catch (parseError) {
        console.error('Failed to parse JSON response:', parseError);
        console.error('Response status:', response.status);
        console.error('Response headers:', Object.fromEntries(response.headers.entries()));
        throw new Error('Failed to parse server response');
      }
    } catch (error) {
      clearTimeout(timeoutId);
      
      // Provide more helpful error messages
      if (error.name === 'AbortError') {
        console.error('API request timeout:', error);
        throw new Error('Request timeout - operation took too long');
      }
      
      console.error('API request failed:', url, error);
      throw error;
    }
  }

  // Tours
  async getTours(filters = {}) {
    const params = new URLSearchParams();
    if (filters.status) params.append('status', filters.status);
    if (filters.lang) params.append('lang', filters.lang);
    if (filters.base_id) params.append('base_id', filters.base_id);
    
    const query = params.toString();
    return this.request(`/api/admin/tours${query ? `?${query}` : ''}`);
  }

  async getTour(id) {
    return this.request(`/api/admin/tours/${id}`);
  }

  async createTour(data) {
    return this.request('/api/admin/tours', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateTour(id, data) {
    return this.request(`/api/admin/tours/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteTour(id) {
    return this.request(`/api/admin/tours/${id}`, {
      method: 'DELETE',
    });
  }

  async publishTour(id) {
    return this.request(`/api/admin/tours/${id}/publish`, {
      method: 'POST',
    });
  }

  async unpublishTour(id) {
    return this.request(`/api/admin/tours/${id}/unpublish`, {
      method: 'POST',
    });
  }

  async refreshShareID(id) {
    return this.request(`/api/admin/tours/${id}/refresh-share-id`, {
      method: 'POST',
    });
  }

  // Routes
  async computeRoute(tourId, waypoints, profile = 'walk') {
    console.log('[API] Computing route with profile:', profile, 'waypoints:', waypoints.length);
    return this.request(`/api/admin/tours/${tourId}/route`, {
      method: 'PUT',
      body: JSON.stringify({ waypoints, profile }),
    });
  }

  async getRoute(tourId) {
    return this.request(`/api/admin/tours/${tourId}/route`);
  }

  // POI Discovery
  async discoverPOIs(request) {
    return this.request('/api/admin/pois/discover', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  async discoverPOIsByBbox(bbox, lang = 'en', categoryMode = 'touristic', filters = [], limit = 100) {
    return this.discoverPOIs({
      mode: 'bbox',
      bbox,
      lang,
      category_mode: categoryMode,
      filters,
      limit,
    });
  }

  async discoverPOIsByRoute(tourId, radiusM = 100, lang = 'en', categoryMode = 'touristic', filters = [], samplingM = 200, limit = 100) {
    return this.discoverPOIs({
      mode: 'route',
      tour_id: tourId,
      radius_m: radiusM,
      lang,
      category_mode: categoryMode,
      filters,
      sampling_m: samplingM,
      limit,
    });
  }

  async discoverPOIsBySearch(query, bbox = null, lang = 'en', categoryMode = 'touristic', filters = [], limit = 100) {
    return this.discoverPOIs({
      mode: 'search',
      query,
      bbox, // null for global search, bbox object for scoped search
      lang,
      category_mode: categoryMode,
      filters,
      limit,
    });
  }

  async resolveURL(url) {
    return this.request('/api/admin/pois/resolve-url', {
      method: 'POST',
      body: JSON.stringify({ url }),
    });
  }

  // Saved POIs
  async savePOI(tourId, poi) {
    return this.request(`/api/admin/tours/${tourId}/pois`, {
      method: 'POST',
      body: JSON.stringify(poi),
    });
  }

  async getSavedPOIs(tourId) {
    return this.request(`/api/admin/tours/${tourId}/pois`);
  }

  async getSavedPOI(tourId, poiId) {
    return this.request(`/api/admin/tours/${tourId}/pois/${poiId}`);
  }

  async updateSavedPOI(tourId, poiId, data) {
    return this.request(`/api/admin/tours/${tourId}/pois/${poiId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteSavedPOI(tourId, poiId) {
    return this.request(`/api/admin/tours/${tourId}/pois/${poiId}`, {
      method: 'DELETE',
    });
  }

  // Narrations
  async generateNarration(pointId, options = {}) {
    return this.request(`/api/admin/points/${pointId}/narration/generate`, {
      method: 'POST',
      body: JSON.stringify(options),
    });
  }

  async regenerateNarration(pointId, baseVersion, changes = '', maxChars = 500, model = '') {
    return this.request(`/api/admin/points/${pointId}/narration/regenerate`, {
      method: 'POST',
      body: JSON.stringify({ base_version: baseVersion, changes, max_chars: maxChars, model }),
    });
  }

  async updateNarration(narrationId, text) {
    return this.request(`/api/admin/narrations/${narrationId}`, {
      method: 'PATCH',
      body: JSON.stringify({ text }),
    });
  }

  async getNarrations(pointId) {
    return this.request(`/api/admin/points/${pointId}/narrations`);
  }

  async activateNarration(narrationId) {
    return this.request(`/api/admin/narrations/${narrationId}/activate`, {
      method: 'POST',
    });
  }

  // TTS Audio
  async getVoices() {
    return this.request('/api/admin/voices');
  }

  async generateAudio(narrationId, voice = 'alloy', model = null) {
    const body = { voice };
    if (model) {
      body.model = model;
    }
    return this.request(`/api/admin/narrations/${narrationId}/audio`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  async getAudioAsset(assetId) {
    return this.request(`/api/admin/audio/${assetId}`);
  }

  async getAudioByNarrationId(narrationId) {
    return this.request(`/api/admin/audio/narration/${narrationId}`);
  }

  async fetchAudioBlob(assetId) {
    const url = `${this.baseUrl}/api/admin/audio/${assetId}/download`;
    const headers = {
      'Authorization': `Bearer ${this.getToken()}`,
    };

    try {
      const response = await fetch(url, { headers });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch audio: HTTP ${response.status}`);
      }

      return await response.blob();
    } catch (error) {
      console.error('Audio fetch failed:', error);
      throw error;
    }
  }

  // Public API (no authentication required)
  async getPublicTour(shareId) {
    const url = `${this.baseUrl}/api/public/tours/${shareId}`;
    
    try {
      const response = await fetch(url, {
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('Tour not found or not published');
        }
        throw new Error(`HTTP ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to fetch public tour:', error);
      throw error;
    }
  }
}

export const api = new ApiClient();
export default api;
